import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';

function Results() {
    const [cars, setCars] = useState([]); // State to store cars data
    const [primaryPhotos, setPrimaryPhotos] = useState([]); // State to store primary photos
    const [loading, setLoading] = useState(false); // State to track loading
    const [error, setError] = useState(null); // State to track errors
    const location = useLocation();
    const navigate = useNavigate();

    // Extract query parameters and initial values
    const queryParams = new URLSearchParams(location.search);
    const initialLocation = queryParams.get('location') || 'Kelowna';
    const initialStartDate = queryParams.get('start_date');
    const initialEndDate = queryParams.get('end_date');

    const backendBaseUrl = 'https://api.karrentals.ca';

    useEffect(() => {
        document.title = "Search Results | KAR – Kelowna Auto Rentals";
        document.querySelector('meta[name="description"]')?.setAttribute("content",
            "Find available rental cars in Kelowna and Vancouver. Browse KAR's premium fleet — Corvette, Tesla, Ford Raptor, Bronco & more. Book online today."
        );
    }, []);

    // Calculate total price
    const calculateTotalPrice = (pricePerDay, startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)); // Difference in days
        return pricePerDay * Math.max(days, 1); // Ensure at least 1 day
    };

    useEffect(() => {
        const fetchCars = async () => {
            setLoading(true);
            setError(null);

            if (!initialStartDate || !initialEndDate) {
                setError('Missing search parameters.');
                setLoading(false);
                return;
            }

            try {
                const response = await axios.get(`${backendBaseUrl}/search-cars`, {
                    params: {
                        location: initialLocation,
                        start_date: initialStartDate,
                        end_date: initialEndDate,
                    },
                });

                if (response.data?.success && Array.isArray(response.data.data)) {
                    const updatedCars = response.data.data.map((car) => ({
                        ...car,
                        totalPrice: calculateTotalPrice(car.price_per_day, initialStartDate, initialEndDate),
                    }));
                    setCars(updatedCars);
                } else {
                    setCars([]);
                    console.error('Unexpected API response:', response.data);
                }
            } catch (err) {
                console.error(err);
                setError('Unable to fetch car data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchCars();
    }, [initialLocation, initialStartDate, initialEndDate]);

    useEffect(() => {
        const fetchPrimaryPhotos = async () => {
            try {
                const response = await axios.get('https://api.karrentals.ca/primary-photos');
                if (response.data.success) {
                    setPrimaryPhotos(response.data.data);
                } else {
                    console.error('Unexpected API response:', response.data);
                }
            } catch (error) {
                console.error('Error fetching primary photos:', error);
            }
        };

        fetchPrimaryPhotos();
    }, []);

    const handleSearch = (location, startDateTime, endDateTime) => {
        navigate(
            `/results?location=${location}&start_date=${encodeURIComponent(
                startDateTime
            )}&end_date=${encodeURIComponent(endDateTime)}`,
            { state: { startDate: startDateTime, endDate: endDateTime } }
        );
    };

    return (
        <Layout>
            <div className="container mx-auto">
                {/* Search Bar Section */}
                <SearchBar
                    initialLocation={initialLocation}
                    initialStartDate={initialStartDate ? new Date(initialStartDate) : null}
                    initialEndDate={initialEndDate ? new Date(initialEndDate) : null}
                    onSearch={handleSearch}
                />

                {/* Results Section */}
                <div className="relative container mx-auto -mt-64 shadow-lg px-2 py-8 z-5 mb-10">
                    <div className="bg-white rounded-2xl p-4">
                        <h1 className="text-3xl font-bold text-black my-4">Search Results</h1>
                        {loading && <p>Loading cars...</p>}
                        {error && <p className="text-red-500">{error}</p>}
                        {!loading && !error && cars.length === 0 && (
                            <div className="text-center py-10">
                                <h2 className="text-2xl font-bold text-gray-700">
                                    No cars available for the selected criteria.
                                </h2>
                                <p className="text-gray-600 mt-2">
                                    Please adjust your search filters or try different dates.
                                </p>
                                <button
                                    onClick={() => navigate('/')}
                                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                    Start a New Search
                                </button>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {cars.map((car) => {
                                // Find the primary photo for the current car
                                const primaryPhoto = primaryPhotos.find(
                                    (photo) => photo.car_id === car.id
                                )?.primary_photo_url;

                                return (
                                    <div
                                        key={car.id}
                                        className="bg-black text-white rounded-lg shadow-lg overflow-hidden flex flex-col"
                                    >
                                        <img
                                            src={primaryPhoto || 'https://via.placeholder.com/400x300?text=No+Image'}
                                            alt={`Car ${car.name}`}
                                            className="h-40 w-full object-cover"
                                        />
                                        <div className="p-4">
                                            <h2 className="text-xl font-bold mb-2">
                                                {car.year} {car.name} {car.model}
                                            </h2>
                                            <p className="text-lg text-right">
                                                Total: ${car.totalPrice}
                                            </p>
                                            <button
                                                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                onClick={() =>
                                                    navigate(`/car/${car.id}`, {
                                                        state: {
                                                            startDate: initialStartDate,
                                                            endDate: initialEndDate,
                                                        },
                                                    })
                                                }
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default Results;
